import { useEffect, useRef, useState } from "react";
import { bySex } from "@/lib/bySex";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Loader2, Sparkles, Luggage, Heart, Check, ExternalLink, Plus, RefreshCw, Copy, Tag, X, Search, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CheckoutButton } from "@/components/CheckoutButton";

const BAG_DISCOUNT = 0.2;
const BAG_CODE = "VALIGIA20";
const MIN_BAG = 4;

export default function HospitalBag() {
  const { profile, addManyToWishlist, inWishlist } = useProfile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [optLoading, setOptLoading] = useState(false);
  const [q, setQ] = useState("");
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const ob = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.05 });
    ob.observe(el);
    return () => ob.disconnect();
  }, [data?.items?.length]);
  const [codes, setCodes] = useState(null);

  const removeItem = async (pid) => {
    try {
      const { data: upd } = await api.delete(`/hospital-bag/${profile.id}/item/${pid}`);
      setData(upd);
      toast.success("Prodotto rimosso dalla valigia");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile rimuovere il prodotto");
    }
  };

  const openAdd = async () => {
    setAddOpen(true);
    setOptLoading(true);
    setQ("");
    try {
      const { data: od } = await api.get(`/hospital-bag/${profile.id}/options`);
      setOptions(od.options || []);
    } catch {
      toast.error("Impossibile caricare i prodotti");
    } finally {
      setOptLoading(false);
    }
  };

  const addItem = async (pid) => {
    try {
      const { data: upd } = await api.post(`/hospital-bag/${profile.id}/item`, { product_id: String(pid) });
      setData(upd);
      setOptions((o) => o.filter((p) => String(p.id) !== String(pid)));
      toast.success("Prodotto aggiunto alla valigia");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile aggiungere il prodotto");
    }
  };

  const load = (refresh = false) => {
    if (!profile) return;
    refresh ? setRegenerating(true) : setLoading(true);
    api.get("/hospital-bag", { params: { profile_id: profile.id, refresh } })
      .then(({ data }) => setData(data))
      .catch(() => setData(false))
      .finally(() => { setLoading(false); setRegenerating(false); });
  };

  useEffect(() => { load(false); }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (profile) api.get(`/profile/${profile.id}/discount-codes`).then(({ data }) => setCodes(data)).catch(() => {});
  }, [profile]);

  if (!profile) return null;

  const subject = profile.baby_name || bySex(profile.sex, { femmina: "la tua bambina", maschio: "il tuo bambino", other: "il tuo bebè" });

  const addAll = () => {
    if (!data?.items?.length) return;
    addManyToWishlist(data.items);
  };

  const subtotal = (data?.items || []).reduce(
    (sum, p) => sum + (parseFloat(p.price) || 0) * (p.quantity || 1), 0
  );
  const discount = Math.floor(subtotal * BAG_DISCOUNT * 100 + 1e-6) / 100;
  const total = subtotal - discount;
  const fmt = (n) => `€ ${n.toFixed(2)}`;

  const bagCode = codes?.bag?.code || BAG_CODE;
  const copyCode = () => {
    navigator.clipboard.writeText(bagCode);
    setCopied(true);
    toast.success(`Codice ${bagCode} copiato!`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="valigia" className="bg-[#F4EFEA] scroll-mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-20">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
          <div>
            <div className="flex items-center gap-2 text-[#C87D75] mb-2">
              <Luggage size={18} />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">Valigia per l'ospedale · AI</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2825]">La valigia per {subject}, dal nostro negozio</h2>
            <p className="text-[#5C534E] mt-2 max-w-xl">
              La nostra AI ha esaminato il catalogo e scelto i capi disponibili più adatti per la nascita.
              <span className="block text-sm text-[#8C7F78] mt-1">
                Puoi personalizzarla: aggiungi o togli i prodotti che vuoi (minimo {MIN_BAG}).
              </span>
            </p>
          </div>
          {data && data.items?.length > 0 && (
            <div className="flex flex-wrap gap-3 self-start">
              <button data-testid="bag-add-product" onClick={openAdd}
                className="inline-flex items-center gap-2 rounded-full border border-[#4E7363] text-[#4E7363] hover:bg-white px-5 py-2.5 text-sm transition-colors">
                <Plus size={15} /> Aggiungi prodotto
              </button>
              <button data-testid="bag-regenerate" onClick={() => load(true)} disabled={regenerating}
                className="inline-flex items-center gap-2 rounded-full border border-[#D2C5B9] text-[#5C534E] hover:bg-white px-5 py-2.5 text-sm transition-colors">
                {regenerating ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />} Rigenera
              </button>
              <button data-testid="bag-add-all" onClick={addAll}
                className="inline-flex items-center gap-2 rounded-full bg-[#E5A8A0] hover:bg-[#C87D75] text-white px-5 py-2.5 text-sm transition-colors">
                <Plus size={15} /> Aggiungi tutta la valigia
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-[#8C7F78] py-10" data-testid="bag-loading">
            <Loader2 className="animate-spin" size={18} /> La nostra AI sta componendo la valigia dal catalogo...
          </div>
        )}

        {data && data.items && (
          <div data-testid="hospital-bag">
            <motion.div
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="rounded-2xl bg-white border border-[#EAE3DC] p-6 mb-8"
            >
              <div className="flex items-center gap-2 text-[#C87D75] mb-2">
                <Sparkles size={16} /><span className="font-display font-medium text-sm">Il consiglio per te</span>
              </div>
              <p className="text-[#5C534E] leading-relaxed" data-testid="bag-intro">{data.intro}</p>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-[#8C7F78]">
                <span className="rounded-full bg-[#EAF0EC] text-[#4E7363] px-3 py-1">Stagione {data.season}</span>
                <span className="rounded-full bg-[#F9ECE9] text-[#C87D75] px-3 py-1">Taglia {data.recommended_size}</span>
                <span className="rounded-full bg-[#FDF6E7] text-[#C29638] px-3 py-1">{data.items.length} capi scelti</span>
              </div>
            </motion.div>

            {data.items.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                data-testid="bag-summary"
                className="rounded-2xl p-6 mb-8 text-white relative overflow-hidden"
                style={{ background: "linear-gradient(145deg,#E5A8A0,#C87D75)" }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                  <div>
                    <div className="flex items-center gap-2 text-white/90 text-xs uppercase tracking-widest">
                      <Tag size={14} /> Totale stimato della valigia
                    </div>
                    <div className="mt-2 flex items-end gap-3">
                      <span className="font-serif text-5xl" data-testid="bag-total">{fmt(total)}</span>
                      <span className="text-white/70 line-through mb-1.5" data-testid="bag-subtotal">{fmt(subtotal)}</span>
                    </div>
                    <div className="text-white/90 text-sm mt-1" data-testid="bag-discount">
                      Risparmi {fmt(discount)} con lo sconto valigia del 20%
                    </div>
                  </div>
                  <div className="sm:text-right">
                    <CheckoutButton source="bag" testId="bag-checkout" label="Acquista la valigia"
                      className="rounded-full bg-[#2D2825] text-white hover:bg-[#473f3a] px-6 py-3 text-sm font-medium" />
                    <div className="text-white/80 text-xs mt-2 mb-2">Sconto del 20% applicato in automatico al checkout · minimo 4 articoli · utilizzabile una sola volta, non cumulabile</div>
                    <button onClick={copyCode} data-testid="bag-copy-code"
                      className="inline-flex items-center gap-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors px-4 py-1.5 text-sm font-semibold tracking-widest">
                      {bagCode} {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                    <button onClick={addAll} data-testid="bag-summary-add-all"
                      className="mt-3 block sm:ml-auto inline-flex items-center gap-2 rounded-full bg-white text-[#C87D75] hover:bg-[#FFF3F1] transition-colors px-5 py-2.5 text-sm font-medium">
                      <Plus size={15} /> Aggiungi tutta la valigia alla lista
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={sectionRef} className="mb-rail mb-16 sm:mb-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6" data-testid="bag-products">
              {data.items.map((p, i) => (
                <motion.div
                  key={p.id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }}
                  data-testid="bag-product-card"
                  className="group rounded-2xl bg-white border border-[#EAE3DC] overflow-hidden flex flex-col"
                >
                  <div className="relative aspect-square overflow-hidden bg-[#FAF8F5]">
                    {p.image && <img src={p.image} alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                    <span className="absolute top-2 left-2 sm:top-3 sm:left-3 rounded-full bg-white/90 backdrop-blur text-[#4E7363] text-[10px] sm:text-[11px] px-2 py-0.5 sm:px-2.5 sm:py-1">
                      {p.category}
                    </span>
                    {p.best_seller && (
                      <span data-testid={`bag-bestseller-${p.id}`} className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 inline-flex items-center gap-1 rounded-full bg-[#E9C46A] text-[#2D2825] text-[10px] sm:text-[11px] px-2 py-0.5 sm:px-2.5 sm:py-1 font-medium">
                        <Star size={11} className="fill-[#2D2825]" /> Best seller
                      </span>
                    )}
                    <button data-testid={`bag-remove-${p.id}`} onClick={() => removeItem(p.id)}
                      aria-label="Rimuovi dalla valigia"
                      className="absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/90 backdrop-blur text-[#B85750] hover:bg-[#B85750] hover:text-white shadow-sm transition-colors">
                      <X size={15} />
                    </button>
                    {p.quantity > 1 && (
                      <span className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 rounded-full bg-[#2D2825] text-white text-[10px] sm:text-[11px] px-2 py-0.5 sm:px-2.5 sm:py-1">
                        x{p.quantity}
                      </span>
                    )}
                  </div>
                  <div className="p-3 sm:p-4 flex flex-col flex-1">
                    <h3 className="font-display text-[13px] sm:text-[15px] font-medium text-[#2D2825] leading-snug line-clamp-2 min-h-[36px] sm:min-h-[42px]">
                      {p.title}
                    </h3>
                    <div className="mt-1.5 text-[10px] sm:text-[11px] text-[#8C7F78] flex flex-wrap items-center gap-1">
                      <span className="rounded-full bg-[#F4EFEA] px-2 py-0.5 text-[#5C534E]">{p.material}</span>
                      <span className="hidden sm:inline">· {p.season_label}</span>
                    </div>
                    {p.reason && (
                      <p className="mt-2 text-[11px] sm:text-[12px] text-[#8C7F78] italic leading-snug line-clamp-2">“{p.reason}”</p>
                    )}
                    <div className="mt-2 font-serif text-xl sm:text-2xl text-[#C87D75]">{p.price ? `€ ${p.price}` : "—"}</div>
                    <div className="mt-auto pt-3 sm:pt-4 flex flex-col sm:flex-row gap-2">
                      <button
                        data-testid={`bag-add-${p.id}`} onClick={() => addManyToWishlist([p])}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-[#E5A8A0] text-[#C87D75] hover:bg-[#F9ECE9] text-sm min-h-[44px] sm:min-h-0 py-2 transition-colors"
                      >
                        {inWishlist(p.id) ? <><Check size={14} /> In lista</> : <><Heart size={14} /> Lista</>}
                      </button>
                      <Link to={`/prodotto/${p.id}`} data-testid={`bag-view-${p.id}`}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full bg-[#2D2825] text-white hover:bg-[#473f3a] text-sm px-3 min-h-[44px] sm:min-h-0 py-2 transition-colors">
                        Vedi <ExternalLink size={13} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {data.items.length === 0 && (
              <p className="text-[#8C7F78] py-8">Al momento non ci sono capi disponibili per comporre la valigia.</p>
            )}
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="bag-add-dialog" className="sm:max-w-2xl bg-[#FAF8F5] max-h-[88vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2D2825]">Aggiungi un prodotto alla valigia</DialogTitle>
            <DialogDescription className="text-[#8C7F78]">
              In cima trovi i capi consigliati dall'AI per stagione e taglia. Tocca “Aggiungi” per inserirli.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C7F78]" />
            <Input data-testid="bag-add-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome o categoria…" className="pl-9 rounded-xl bg-white border-[#EAE3DC]" />
          </div>
          <div className="flex-1 overflow-y-auto mt-3 space-y-2 pr-1" data-testid="bag-options-list">
            {optLoading ? (
              <div className="flex items-center gap-2 text-[#8C7F78] py-10 justify-center">
                <Loader2 className="animate-spin" size={18} /> Carico i prodotti…
              </div>
            ) : (
              (() => {
                const term = q.trim().toLowerCase();
                const list = options.filter((p) =>
                  !term || (p.title || "").toLowerCase().includes(term) || (p.category || "").toLowerCase().includes(term));
                if (list.length === 0) return <p className="text-sm text-[#8C7F78] py-8 text-center">Nessun prodotto trovato.</p>;
                return list.slice(0, 60).map((p) => (
                  <div key={p.id} data-testid={`bag-option-${p.id}`}
                    className={`flex items-center gap-3 rounded-xl bg-white border p-2.5 ${p.recommended ? "border-[#C87D75] ring-1 ring-[#E5A8A0]/40" : "border-[#EAE3DC]"}`}>
                    {p.image && <img src={p.image} alt={p.title} className="w-14 h-14 rounded-lg object-cover" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#2D2825] line-clamp-1 flex items-center gap-1.5">
                        {p.recommended && (
                          <span data-testid={`bag-option-badge-${p.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#F9ECE9] text-[#C87D75] text-[10px] px-2 py-0.5 shrink-0">
                            <Sparkles size={10} /> Consigliato
                          </span>
                        )}
                        <span className="truncate">{p.title}</span>
                      </div>
                      <div className="text-xs text-[#8C7F78]">{p.category} · {p.season_label}{p.price ? ` · € ${p.price}` : ""}</div>
                    </div>
                    <button data-testid={`bag-option-add-${p.id}`} onClick={() => addItem(p.id)}
                      className="inline-flex items-center gap-1 rounded-full bg-[#4E7363] hover:bg-[#3d5a4e] text-white px-4 py-1.5 text-sm transition-colors">
                      <Plus size={14} /> Aggiungi
                    </button>
                  </div>
                ));
              })()
            )}
          </div>
        </DialogContent>
      </Dialog>
      {inView && data?.items?.length > 0 && (
        <div data-testid="bag-sticky-bar"
          className="sm:hidden fixed inset-x-0 bottom-[64px] z-30 px-3 pb-2">
          <div className="flex items-center gap-3 rounded-2xl bg-[#2D2825] text-white px-4 py-2.5 shadow-xl">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-white/60">Valigia · -20%</div>
              <div className="font-serif text-xl leading-tight" data-testid="bag-sticky-total">≈ {fmt(total)}</div>
            </div>
            <CheckoutButton source="bag" testId="bag-sticky-checkout" label="Acquista"
              className="rounded-full bg-[#E5A8A0] hover:bg-[#C87D75] text-white px-5 min-h-[44px] text-sm font-medium" />
          </div>
        </div>
      )}
    </section>
  );
}
